/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { bbox } from '@turf/turf';
import type { Feature, FeatureCollection, GeoJsonProperties } from 'geojson';
import type { TFunction } from 'i18next';
import type { Map } from 'ol';
import proj4 from 'proj4';
import React, { useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { CoreAppStore } from '@opengeoweb/store';
import { layerActions, layerSelectors, mapSelectors } from '@opengeoweb/store';
import { generateLayerId, LayerType } from '@opengeoweb/webmap';
import {
  emptyGeoJSON,
  MapFeatureClass,
  setMapCenter,
  MapContext,
} from '@opengeoweb/webmap-react';
import type { LocationDetail } from './types';
import { useCoreTranslation } from '../../utils/i18n';

export const geometryStyling: GeoJsonProperties = {
  fill: '#FF7800',
  'fill-opacity': 0.25,
  stroke: '#FF7800',
  'stroke-opacity': 1,
  'stroke-width': 4,
};

export const bufferFromZoomLevel = (
  zoom: number,
  lat: number,
  viewportWidthPx: number,
): number => {
  const earthCircumference = 40075016.686;
  const worldPx = 256 * 2 ** zoom;
  const metersPerPx = earthCircumference / worldPx;
  const viewportWidthMeters = metersPerPx * viewportWidthPx;
  const metersPerDegree = 111320 * Math.cos((lat * Math.PI) / 180);
  // Buffer in degrees (half the viewport width)
  const buffer = viewportWidthMeters / metersPerDegree / 2;
  return buffer;
};

export const bboxFromPolygon = (
  geom: GeoJSON.Geometry,
  buffer: number,
): number[] => {
  // A bbox is calculated with a buffer (expressed in %)
  const [minX, minY, maxX, maxY] = bbox(geom);

  const width = maxX - minX;
  const height = maxY - minY;

  return [
    minX - width * buffer,
    minY - height * buffer,
    maxX + width * buffer,
    maxY + height * buffer,
  ];
};

export const bboxFromPoint = (
  geom: GeoJSON.Geometry,
  buffer: number,
): number[] => {
  if (geom.type !== 'Point' || !Array.isArray(geom.coordinates)) {
    throw new Error('bboxFromPoint: geometry must be of type Point');
  }
  const [lon, lat] = geom.coordinates as [number, number];
  const width = 0.01;
  const height = 0.01;
  return [
    lon - width * buffer,
    lat - height * buffer,
    lon + width * buffer,
    lat + height * buffer,
  ];
};

const makeGeoJSONFeatureFromLocation = (
  location: LocationDetail,
  mapFeatureClass: MapFeatureClass,
  t: TFunction,
): FeatureCollection => {
  const collection: FeatureCollection = {
    type: 'FeatureCollection',
    features: [],
  };

  const name =
    mapFeatureClass === MapFeatureClass.MyLocation
      ? t('my-location')
      : location.name;

  const geometry: Feature = {
    ...location.geometry!,
    properties: {
      ...geometryStyling,
      name,
      mapFeatureClass,
      showNameOnHover: true,
    },
  };

  const marker: Feature = {
    type: 'Feature',
    properties: {
      name,
      mapFeatureClass,
      showNameOnHover: true,
    },
    geometry: {
      type: 'Point',
      coordinates: [location.lon, location.lat],
    },
  };

  switch (location.geometry?.geometry.type) {
    case 'Point':
    case undefined:
      collection.features.push(marker);
      break;
    default:
      collection.features.push(geometry);
  }

  return collection;
};

export const useShiftPressed = (): boolean => {
  const [shiftIsPressed, setShiftIsPressed] = React.useState(false);
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Shift') {
        setShiftIsPressed(true);
      }
    };
    const handleKeyUp = (event: KeyboardEvent): void => {
      if (event.key === 'Shift') {
        setShiftIsPressed(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return (): void => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);
  return shiftIsPressed;
};

const setBbox = (map: Map, bbox: number[]): void => {
  const to = map.getView().getProjection().getCode();
  const extent = [
    ...proj4(to, [bbox[0], bbox[1]]),
    ...proj4(to, [bbox[2], bbox[3]]),
  ];
  map.getView().fit(extent);
};

export const useDrawOnMap = (
  mapId: string,
): [(location?: LocationDetail) => void] => {
  const dispatch = useDispatch();
  const { t } = useCoreTranslation();
  const shiftIsPressed = useShiftPressed();

  const { map } = useContext(MapContext);

  const layerIdRef = React.useRef<string>(undefined);
  React.useEffect(() => {
    layerIdRef.current = generateLayerId();
  }, []);
  const layerId = layerIdRef.current;

  const isMapPresent = useSelector((store: CoreAppStore) =>
    mapSelectors.getIsMapPresent(store, mapId),
  );
  const isLayerPresent = useSelector((store: CoreAppStore) =>
    layerSelectors.getIsLayerPresent(store, layerIdRef.current),
  );
  const shouldAddLayer = isMapPresent && !isLayerPresent && layerId;

  React.useEffect(() => {
    if (shouldAddLayer) {
      dispatch(
        layerActions.addLayer({
          mapId,
          layerId,
          layer: {
            geojson: emptyGeoJSON,
            layerType: LayerType.featureLayer,
          },
          origin: 'LocationSearchConnect',
        }),
      );
    }
  }, [dispatch, layerId, mapId, shouldAddLayer]);

  const updateGeoJSON = React.useCallback(
    (location?: LocationDetail): void => {
      const mapFeatureClass =
        location?.source === 'MyMapLocation'
          ? MapFeatureClass.MyLocation
          : MapFeatureClass.GenericMarker;
      const geojson = location
        ? makeGeoJSONFeatureFromLocation(location, mapFeatureClass, t)
        : emptyGeoJSON;
      dispatch(layerActions.updateFeature({ layerId: layerId!, geojson }));
    },
    [dispatch, layerId, t],
  );

  const zoomToFeature = React.useCallback(
    (location: LocationDetail): void => {
      if (map) {
        const defaultZoomLevel = 12;
        setMapCenter(
          map,
          location.lon,
          location.lat,
          shiftIsPressed ? defaultZoomLevel : undefined,
        );
        if (shiftIsPressed && location.bbox) {
          setBbox(map, location.bbox);
          return;
        }
        if (shiftIsPressed && location.geometry?.geometry) {
          const mapSize = map.getSize();
          if (mapSize) {
            const buffer = bufferFromZoomLevel(
              defaultZoomLevel,
              location.lat,
              mapSize[0],
            );
            const bbox =
              location.geometry.geometry.type === 'Point'
                ? bboxFromPoint(location.geometry.geometry, buffer)
                : bboxFromPolygon(location.geometry.geometry, buffer);
            setBbox(map, bbox);
          }
        }
      }
    },
    [map, shiftIsPressed],
  );

  const onUpdateLocation = React.useCallback(
    (location?: LocationDetail): void => {
      updateGeoJSON(location);
      if (location) {
        zoomToFeature(location);
      }
    },
    [updateGeoJSON, zoomToFeature],
  );
  return [onUpdateLocation];
};
